/**
 * GLANCE HERO — the mobile home's "Right Now" surface (docs/design/
 * reimagine-glance-home.md). Fixed eye-order: a context line (where/when), the
 * ONE thing that matters this minute (leave-now → next commitment → quiet
 * anchor), and the derived concierge sentence (buildDayBrief). The boarding-pass
 * CREDENTIAL state is the TravelCard fast-lane overlay; this hero owns the
 * show-day / non-travel-window states. Rest-of-day is the Rail below it. Tap
 * opens the focus gig. All field/collection names via the registry (CONSTRAINTS
 * #5); gig-header fields (city) are client-owned literals like Today/Rail.
 */
import { registry } from "@td/registry";
import { t, type Locale } from "../i18n.ts";
import { computeVerdict, wallClock, type TodayFocus } from "./Today.tsx";
import { buildDayRun, entryLabel, entryPlace } from "./dayRun.ts";
import { buildDayBrief } from "./dayBrief.ts";
import { gigDisplayName } from "./GigList.tsx";

function s(v: unknown): string {
  return typeof v === "string" ? v : "";
}

function weekday(locale: Locale, day: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(day);
  if (m === null) return "";
  return new Intl.DateTimeFormat(locale === "fr" ? "fr-FR" : "en-GB", { weekday: "long", timeZone: "UTC" }).format(
    new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]))),
  );
}

export interface GlanceHeroProps {
  locale: Locale;
  focus: TodayFocus;
  nowMs: number;
  onOpen(gigId: string): void;
}

export function GlanceHero({ locale, focus, nowMs, onOpen }: GlanceHeroProps) {
  const clock = wallClock(nowMs, focus.tz);
  const city = s(focus.gig.state.gig?.fields["city"]) || gigDisplayName(focus.gig);
  const verdict = computeVerdict(nowMs, focus.tz, { day: focus.day }, focus.next);

  const dayGroup = buildDayRun(focus.gig).find((g) => g.day === focus.day);
  const brief = dayGroup !== undefined ? buildDayBrief(dayGroup.entries, focus.gig, locale) : "";

  // Priority: leave-now verdict → next commitment → the day's quiet anchor.
  let kind: "leave" | "next" | "quiet";
  let big = ""; // the heavy line (countdown / time), rendered mono
  let label = "";
  let sub = "";
  if (verdict !== null && (verdict.key === "today.verdict.leave" || verdict.key === "today.verdict.now")) {
    kind = "leave";
    big = verdict.key === "today.verdict.now" ? t(locale, "glance.now") : (verdict.leaveText ?? "");
    label = t(locale, "glance.leave");
    sub = focus.next !== null ? [entryLabel(focus.next), entryPlace(focus.next)].filter(Boolean).join(" · ") : "";
  } else if (focus.next !== null) {
    kind = "next";
    big = focus.next.time;
    label = entryLabel(focus.next) || t(locale, "glance.next_label");
    sub = entryPlace(focus.next);
  } else {
    kind = "quiet";
    label = (focus.hotel !== null ? s(focus.hotel.fields[registry.hotels.fields.name.key]) : "") || gigDisplayName(focus.gig);
    sub = t(locale, "glance.quiet");
  }

  return (
    <button type="button" className={`glance glance-${kind}`} onClick={() => onOpen(focus.gig.gig_id)}>
      <div className="glance-ctx">
        <span className="glance-place">{[city, weekday(locale, focus.day)].filter(Boolean).join(" · ")}</span>
        <span className="glance-clock num">{clock.time}</span>
      </div>
      <div className="glance-hero">
        {big !== "" && <span className="glance-big num">{big}</span>}
        <span className="glance-title">{label}</span>
        {sub !== "" && <span className="glance-sub">{sub}</span>}
      </div>
      {brief !== "" && <div className="glance-brief">{brief}</div>}
    </button>
  );
}
